import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { calculateIkrProgress } from '../../lib/utils';
import KPIProgressBar from '../../components/ui/KPIProgressBar';
import TeamKRRow from './TeamKRRow';

function getStatusInfo(progress) {
  if (progress >= 1.0) return { label: 'Ahead',    badge: 'bg-emerald-100 text-emerald-700' };
  if (progress >= 0.7) return { label: 'On Track', badge: 'bg-green-100 text-green-700'     };
  if (progress >= 0.3) return { label: 'At Risk',  badge: 'bg-amber-100 text-amber-700'     };
  return               { label: 'Behind',  badge: 'bg-red-100 text-red-700'         };
}

export default function KRSection({ kr, onUpdate, onDelete, users, viewer, onOwnerAssigned }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [krText, setKrText] = useState(kr.text);
  const canEdit = viewer.role === 'Admin';

  const progress = useMemo(() => {
    const allIkrs = (kr.teamKRs || []).flatMap(tkr => tkr.individualKRs || []);
    if (allIkrs.length === 0) return 0;
    return allIkrs.reduce((sum, ikr) => sum + calculateIkrProgress(ikr), 0) / allIkrs.length;
  }, [kr.teamKRs]);

  const handleUpdateTeamKR = (updatedTkr) => {
    const updatedTeamKRs = (kr.teamKRs || []).map(tkr => tkr.id === updatedTkr.id ? updatedTkr : tkr);
    onUpdate({ ...kr, teamKRs: updatedTeamKRs });
  };

  const handleDeleteTeamKR = (tkrId) => {
    onUpdate({ ...kr, teamKRs: (kr.teamKRs || []).filter(tkr => tkr.id !== tkrId) });
  };

  const addTeamKR = () => {
    const newTkr = { id: `tkr_${Date.now()}`, text: 'New Team Key Result', targetKPI: '', unit: '', progress: 0, individualKRs: [] };
    onUpdate({ ...kr, teamKRs: [...(kr.teamKRs || []), newTkr] });
    setIsExpanded(true);
  };

  const saveKrText = () => { setIsEditing(false); onUpdate({ ...kr, text: krText }); };

  const status = getStatusInfo(progress);
  const teamKRCount = (kr.teamKRs || []).length;

  return (
    <div className="rounded-lg border border-gray-100 bg-white mb-2 overflow-hidden">

      {/* ── KR Header ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3 py-2.5 px-3 hover:bg-gray-50/70 transition-colors">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-shrink-0 text-gray-400 hover:text-blue-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>

        <span className="text-xs font-mono font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded flex-shrink-0">
          {kr.code}
        </span>

        <div className="flex-grow min-w-0">
          {isEditing ? (
            <input
              type="text"
              value={krText}
              onChange={(e) => setKrText(e.target.value)}
              onBlur={saveKrText}
              onKeyPress={(e) => { if (e.key === 'Enter') saveKrText(); }}
              className="w-full p-1 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          ) : (
            <p
              className={`text-sm font-semibold text-gray-800 truncate ${canEdit ? 'cursor-pointer hover:text-blue-600' : ''}`}
              onClick={() => canEdit && setIsEditing(true)}
            >
              {kr.text}
            </p>
          )}
        </div>

        {/* Right: team KR count + status badge + progress + delete (admin) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {teamKRCount > 0 && (
            <span className="text-xs text-gray-400 hidden sm:inline">
              {teamKRCount} team KR{teamKRCount !== 1 ? 's' : ''}
            </span>
          )}
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.badge}`}>
            {status.label}
          </span>
          <div className="flex items-center gap-1.5 w-28">
            <div className="flex-1"><KPIProgressBar value={progress} /></div>
            <span className="text-xs font-bold w-9 text-right text-gray-700 tabular-nums">
              {(progress * 100).toFixed(0)}%
            </span>
          </div>
          {canEdit && onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(kr.id); }}
              className="text-gray-300 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-colors flex-shrink-0"
              title="Delete key result"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>

      {/* ── Expanded: Team KR Rows ────────────────────────────── */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 bg-gray-50/30">
          {(kr.teamKRs || []).length === 0 && (
            <p className="text-xs text-gray-400 italic py-2 pl-1">No team KRs yet.</p>
          )}
          {(kr.teamKRs || []).map(tkr => (
            <TeamKRRow
              key={tkr.id}
              tkr={tkr}
              onUpdate={handleUpdateTeamKR}
              onDelete={handleDeleteTeamKR}
              users={users}
              viewer={viewer}
              krCode={kr.code}
              onOwnerAssigned={onOwnerAssigned}
            />
          ))}
          {canEdit && (
            <button
              onClick={addTeamKR}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold mt-2 pl-1"
            >
              <Plus size={13} /> Add Team KR
            </button>
          )}
        </div>
      )}
    </div>
  );
}
